/**
 * Construction Insurance Marketplace
 * Compare insurance policies, get quotes, instant coverage for construction projects
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Skeleton,
  Badge,
  Tooltip,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Security,
  Shield,
  CheckCircle,
  CompareArrows,
  RequestQuote,
  Policy,
  Verified,
  Build,
  Engineering,
  AccountBalance,
  Assessment,
  Phone,
  Email,
  WhatsApp,
  Download,
  Schedule,
  LocationOn,
  Business,
  Home,
  LocalHospital,
  Warning,
  Star,
  TrendingUp,
  Calculate
} from '@mui/icons-material';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import { formatCurrency, formatDate } from '../../utils/helpers';
import DialogCloseButton from '../../components/common/DialogCloseButton';

// Mock insurance providers data
const mockInsuranceData = [
  {
    id: 1,
    provider: {
      name: 'Zimbabwe Insurance Group',
      logo: '/api/placeholder/60/60',
      rating: 4.8,
      reviewCount: 245,
      isVerified: true,
      yearsInBusiness: 25,
      licenseNumber: 'INS-ZIM-001',
      location: 'Harare'
    },
    policies: [
      {
        id: 'zig-001',
        name: 'Comprehensive Construction Cover',
        type: 'All Risks',
        description: 'Complete protection for construction projects including materials, equipment, and liability.',
        monthlyPremium: 850,
        annualPremium: 8500,
        coverageAmount: 2000000,
        deductible: 25000,
        features: [
          'Contract Works Coverage',
          'Plant & Equipment',
          'Public Liability',
          'Employer Liability',
          'Professional Indemnity',
          'Defects Liability'
        ],
        coverageDetails: {
          'Contract Works': '100% replacement cost',
          'Public Liability': '$1M per occurrence',
          'Equipment Coverage': '$500K per item',
          'Material Coverage': 'Full replacement',
          'Third Party Property': '$250K',
          'Legal Expenses': '$50K'
        },
        excludes: ['Nuclear risks', 'War & terrorism', 'Inherent defects'],
        claimProcess: '24-hour online claims',
        responseTime: '2 hours',
        isPopular: true,
        discount: 15
      },
      {
        id: 'zig-002',
        name: 'Basic Builder Protection',
        type: 'Named Perils',
        description: 'Essential coverage for smaller construction projects and contractors.',
        monthlyPremium: 450,
        annualPremium: 4500,
        coverageAmount: 750000,
        deductible: 15000,
        features: [
          'Contract Works Coverage',
          'Public Liability',
          'Equipment Protection',
          'Material Damage'
        ],
        coverageDetails: {
          'Contract Works': '90% replacement cost',
          'Public Liability': '$500K per occurrence',
          'Equipment Coverage': '$200K per item',
          'Material Coverage': 'Actual cash value'
        },
        excludes: ['Professional liability', 'Design defects', 'Nuclear risks'],
        claimProcess: 'Online & phone claims',
        responseTime: '4 hours',
        isAffordable: true
      }
    ]
  },
  {
    id: 2,
    provider: {
      name: 'Construction Risk Specialists',
      logo: '/api/placeholder/60/60',
      rating: 4.9,
      reviewCount: 189,
      isVerified: true,
      yearsInBusiness: 18,
      licenseNumber: 'INS-ZIM-002',
      location: 'Bulawayo'
    },
    policies: [
      {
        id: 'crs-001',
        name: 'Premium Project Protection',
        type: 'All Risks Plus',
        description: 'Enhanced coverage designed for large-scale commercial and industrial projects.',
        monthlyPremium: 1200,
        annualPremium: 12000,
        coverageAmount: 5000000,
        deductible: 50000,
        features: [
          'Extended Contract Works',
          'Advanced Plant Coverage',
          'Comprehensive Liability',
          'Design & Build Cover',
          'Delay in Start-up',
          'Marine Transit'
        ],
        coverageDetails: {
          'Contract Works': '110% replacement cost',
          'Public Liability': '$2M per occurrence',
          'Equipment Coverage': '$1M per item',
          'Design Liability': '$500K',
          'Delay Coverage': '$100K per month',
          'Transit Coverage': 'Worldwide'
        },
        excludes: ['Nuclear risks', 'Cyber attacks'],
        claimProcess: 'Dedicated claims manager',
        responseTime: '1 hour',
        isPremium: true,
        discount: 20
      }
    ]
  },
  {
    id: 3,
    provider: {
      name: 'Mutual Assurance Zimbabwe',
      logo: '/api/placeholder/60/60',
      rating: 4.6,
      reviewCount: 167,
      isVerified: true,
      yearsInBusiness: 30,
      licenseNumber: 'INS-ZIM-003',
      location: 'Gweru'
    },
    policies: [
      {
        id: 'maz-001',
        name: 'Small Works Insurance',
        type: 'Basic Coverage',
        description: 'Affordable protection for residential and small commercial projects.',
        monthlyPremium: 280,
        annualPremium: 2800,
        coverageAmount: 500000,
        deductible: 10000,
        features: [
          'Contract Works Coverage',
          'Public Liability',
          'Tool & Equipment',
          'Material Storage'
        ],
        coverageDetails: {
          'Contract Works': '80% replacement cost',
          'Public Liability': '$250K per occurrence',
          'Equipment Coverage': '$50K total',
          'Material Coverage': 'Named perils only'
        },
        excludes: ['Professional liability', 'Design errors', 'Consequential loss'],
        claimProcess: 'Standard processing',
        responseTime: '24 hours',
        isAffordable: true
      }
    ]
  }
];

const InsuranceMarketplace = () => {
  const [insurance, setInsurance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteDialog, setQuoteDialog] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [compareDialog, setCompareDialog] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [quoteStep, setQuoteStep] = useState(0);
  const [quoteDetails, setQuoteDetails] = useState({
    projectType: '',
    projectValue: '',
    duration: '',
    location: '',
    riskLevel: 'medium',
    previousClaims: false,
    additionalCoverage: [],
    contactInfo: {
      name: '',
      email: '',
      phone: '',
      company: ''
    }
  });

  // Load insurance data
  useEffect(() => {
    const loadInsurance = async () => {
      setLoading(true);
      setTimeout(() => {
        // TODO: Replace with real API call to /api/marketplace/insurance
        setInsurance([]); // Empty - no mock data
        setLoading(false);
      }, 500);
    };

    loadInsurance();
  }, []);

  // Handle get quote
  const handleGetQuote = (policy) => {
    setSelectedPolicy(policy);
    setQuoteStep(0);
    setQuoteDialog(true);
  };

  // Handle compare policies
  const handleCompare = (policy) => {
    if (selectedPolicies.includes(policy.id)) {
      setSelectedPolicies(selectedPolicies.filter(id => id !== policy.id));
    } else if (selectedPolicies.length < 3) {
      setSelectedPolicies([...selectedPolicies, policy.id]);
    }
  };

  // Calculate estimated premium
  const calculateEstimatedPremium = (basePolicy, details) => {
    let premium = basePolicy.monthlyPremium;
    
    // Adjust based on project value
    const projectValue = parseFloat(details.projectValue) || 0;
    if (projectValue > 1000000) premium *= 1.3;
    else if (projectValue > 500000) premium *= 1.15;
    
    // Adjust based on risk level
    if (details.riskLevel === 'high') premium *= 1.4;
    else if (details.riskLevel === 'low') premium *= 0.85;
    
    // Adjust for previous claims
    if (details.previousClaims) premium *= 1.25;
    
    return premium;
  };

  // Quote Dialog Component
  const QuoteDialog = () => {
    const steps = ['Project Details', 'Coverage Options', 'Personal Information', 'Quote Summary'];

    const renderStepContent = () => {
      switch (quoteStep) {
        case 0:
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Project Type</InputLabel>
                  <Select
                    value={quoteDetails.projectType}
                    label="Project Type"
                    onChange={(e) => setQuoteDetails({...quoteDetails, projectType: e.target.value})}
                  >
                    <MenuItem value="residential">Residential Building</MenuItem>
                    <MenuItem value="commercial">Commercial Building</MenuItem>
                    <MenuItem value="industrial">Industrial Facility</MenuItem>
                    <MenuItem value="infrastructure">Infrastructure Project</MenuItem>
                    <MenuItem value="renovation">Renovation/Refurbishment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Project Value (USD)"
                  type="number"
                  value={quoteDetails.projectValue}
                  onChange={(e) => setQuoteDetails({...quoteDetails, projectValue: e.target.value})}
                  placeholder="e.g., 500000"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Project Duration (months)"
                  type="number"
                  value={quoteDetails.duration}
                  onChange={(e) => setQuoteDetails({...quoteDetails, duration: e.target.value})}
                  placeholder="e.g., 18"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Project Location"
                  value={quoteDetails.location}
                  onChange={(e) => setQuoteDetails({...quoteDetails, location: e.target.value})}
                  placeholder="City, Province"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Risk Assessment
                </Typography>
                <RadioGroup
                  value={quoteDetails.riskLevel}
                  onChange={(e) => setQuoteDetails({...quoteDetails, riskLevel: e.target.value})}
                  row
                >
                  <FormControlLabel value="low" control={<Radio />} label="Low Risk" />
                  <FormControlLabel value="medium" control={<Radio />} label="Medium Risk" />
                  <FormControlLabel value="high" control={<Radio />} label="High Risk" />
                </RadioGroup>
              </Grid>
            </Grid>
          );

        case 1:
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Select Additional Coverage
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Choose additional protection options for your project
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <List>
                  {[
                    { id: 'delay', name: 'Delay in Start-up Coverage', premium: 120 },
                    { id: 'environmental', name: 'Environmental Liability', premium: 200 },
                    { id: 'cyber', name: 'Cyber Risk Protection', premium: 80 },
                    { id: 'marine', name: 'Marine Transit Coverage', premium: 150 },
                    { id: 'terrorism', name: 'Terrorism Coverage', premium: 180 }
                  ].map((option) => (
                    <ListItem key={option.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={quoteDetails.additionalCoverage.includes(option.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setQuoteDetails({
                                  ...quoteDetails,
                                  additionalCoverage: [...quoteDetails.additionalCoverage, option.id]
                                });
                              } else {
                                setQuoteDetails({
                                  ...quoteDetails,
                                  additionalCoverage: quoteDetails.additionalCoverage.filter(id => id !== option.id)
                                });
                              }
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              +{formatCurrency(option.premium)}/month
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={quoteDetails.previousClaims}
                      onChange={(e) => setQuoteDetails({...quoteDetails, previousClaims: e.target.checked})}
                    />
                  }
                  label="I have made insurance claims in the past 5 years"
                />
              </Grid>
            </Grid>
          );

        case 2:
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={quoteDetails.contactInfo.name}
                  onChange={(e) => setQuoteDetails({
                    ...quoteDetails,
                    contactInfo: {...quoteDetails.contactInfo, name: e.target.value}
                  })}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={quoteDetails.contactInfo.company}
                  onChange={(e) => setQuoteDetails({
                    ...quoteDetails,
                    contactInfo: {...quoteDetails.contactInfo, company: e.target.value}
                  })}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={quoteDetails.contactInfo.email}
                  onChange={(e) => setQuoteDetails({
                    ...quoteDetails,
                    contactInfo: {...quoteDetails.contactInfo, email: e.target.value}
                  })}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={quoteDetails.contactInfo.phone}
                  onChange={(e) => setQuoteDetails({
                    ...quoteDetails,
                    contactInfo: {...quoteDetails.contactInfo, phone: e.target.value}
                  })}
                />
              </Grid>
            </Grid>
          );

        case 3:
          const estimatedPremium = calculateEstimatedPremium(selectedPolicy, quoteDetails);
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Quote Summary
              </Typography>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    {selectedPolicy?.name}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Project Type</Typography>
                      <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                        {quoteDetails.projectType.replace(/([A-Z])/g, ' $1')}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Project Value</Typography>
                      <Typography variant="body1">
                        {formatCurrency(quoteDetails.projectValue)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Coverage Amount</Typography>
                      <Typography variant="body1">
                        {formatCurrency(selectedPolicy?.coverageAmount)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Deductible</Typography>
                      <Typography variant="body1">
                        {formatCurrency(selectedPolicy?.deductible)}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Base Premium</Typography>
                    <Typography variant="body2">
                      {formatCurrency(selectedPolicy?.monthlyPremium)}/month
                    </Typography>
                  </Box>
                  
                  {quoteDetails.additionalCoverage.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Additional Coverage</Typography>
                      <Typography variant="body2">
                        +{formatCurrency(quoteDetails.additionalCoverage.length * 120)}/month
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Risk Adjustment</Typography>
                    <Typography variant="body2">
                      {estimatedPremium > selectedPolicy?.monthlyPremium ? '+' : ''}
                      {formatCurrency(estimatedPremium - selectedPolicy?.monthlyPremium)}/month
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Estimated Premium</Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(estimatedPremium)}/month
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              
              <Alert severity="info">
                This is an estimated quote. Final premium will be determined after underwriting review.
                You will receive a detailed quote within 24 hours.
              </Alert>
            </Box>
          );

        default:
          return null;
      }
    };

    return (
      <Dialog
        open={quoteDialog}
        onClose={() => setQuoteDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setQuoteDialog(false)} />
        <DialogTitle>
          Get Quote - {selectedPolicy?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Stepper activeStep={quoteStep} sx={{ mb: 2 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {renderStepContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteDialog(false)}>
            Cancel
          </Button>
          {quoteStep > 0 && (
            <Button onClick={() => setQuoteStep(quoteStep - 1)}>
              Back
            </Button>
          )}
          {quoteStep < 3 ? (
            <Button
              variant="contained"
              onClick={() => setQuoteStep(quoteStep + 1)}
              disabled={
                (quoteStep === 0 && (!quoteDetails.projectType || !quoteDetails.projectValue)) ||
                (quoteStep === 2 && (!quoteDetails.contactInfo.name || !quoteDetails.contactInfo.email))
              }
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                alert('Quote request submitted successfully! You will receive a detailed quote within 24 hours.');
                setQuoteDialog(false);
              }}
            >
              Submit Quote Request
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  // Insurance Policy Card Component
  const PolicyCard = ({ provider, policy }) => (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
      }
    }}>
      <CardContent sx={{ pb: 1 }}>
        {/* Provider Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar src={provider.logo} sx={{ width: 40, height: 40, mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {provider.name}
              </Typography>
              {provider.isVerified && <Verified color="primary" sx={{ fontSize: 18 }} />}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Star sx={{ fontSize: 16, color: 'warning.main' }} />
              <Typography variant="caption">
                {provider.rating} ({provider.reviewCount} reviews)
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {policy.isPopular && <Chip label="Popular" size="small" color="warning" sx={{ mb: 0.5 }} />}
            {policy.isPremium && <Chip label="Premium" size="small" color="primary" sx={{ mb: 0.5 }} />}
            {policy.isAffordable && <Chip label="Affordable" size="small" color="success" sx={{ mb: 0.5 }} />}
          </Box>
        </Box>

        {/* Policy Details */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {policy.name}
        </Typography>
        
        <Chip label={policy.type} size="small" color="primary" variant="outlined" sx={{ mb: 2 }} />
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          {policy.description}
        </Typography>

        {/* Pricing */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
              {formatCurrency(policy.monthlyPremium)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              /month
            </Typography>
            {policy.discount && (
              <Chip 
                label={`${policy.discount}% OFF`} 
                size="small" 
                color="error" 
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Annual: {formatCurrency(policy.annualPremium)} • Coverage: {formatCurrency(policy.coverageAmount)}
          </Typography>
        </Box>

        {/* Key Coverage */}
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Key Coverage
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {policy.features.slice(0, 3).map((feature, index) => (
            <Chip key={index} label={feature} size="small" variant="outlined" />
          ))}
          {policy.features.length > 3 && (
            <Chip label={`+${policy.features.length - 3} more`} size="small" variant="outlined" />
          )}
        </Box>

        {/* Quick Info */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Deductible</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(policy.deductible)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Response Time</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {policy.responseTime}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>

      {/* Actions */}
      <Box sx={{ p: 2, pt: 0, mt: 'auto' }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CompareArrows />}
              onClick={() => handleCompare(policy)}
              size="small"
              disabled={selectedPolicies.length >= 3 && !selectedPolicies.includes(policy.id)}
            >
              {selectedPolicies.includes(policy.id) ? 'Remove' : 'Compare'}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<RequestQuote />}
              onClick={() => handleGetQuote(policy)}
              size="small"
            >
              Get Quote
            </Button>
          </Grid>
        </Grid>

        {/* Contact Options */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
          <IconButton size="small" color="success">
            <WhatsApp />
          </IconButton>
          <IconButton size="small" color="primary">
            <Phone />
          </IconButton>
          <IconButton size="small" color="info">
            <Email />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );

  return (
    <>
      <MarketplaceLayout
        category="insurance"
        title="Construction Insurance"
        description="Compare insurance policies from verified providers. Get instant quotes, comprehensive coverage for all construction projects."
        data={insurance}
        loading={loading}
        totalResults={insurance.reduce((total, provider) => total + provider.policies.length, 0)}
        customActions={[
          {
            icon: <Badge badgeContent={selectedPolicies.length} color="primary"><CompareArrows /></Badge>,
            name: 'Compare Policies',
            action: () => setCompareDialog(true)
          },
          {
            icon: <Assessment />,
            name: 'Claims History',
            action: () => console.log('View claims')
          }
        ]}
      >
        {/* Insurance Grid */}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid item key={index} xs={12} sm={6} md={6} lg={4}>
                <Skeleton variant="rectangular" height={450} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {insurance.map((provider) =>
              provider.policies.map((policy) => (
                <Grid item key={policy.id} xs={12} sm={6} md={6} lg={4}>
                  <PolicyCard provider={provider.provider} policy={policy} />
                </Grid>
              ))
            )}
          </Grid>
        )}
      </MarketplaceLayout>

      {/* Quote Dialog */}
      <QuoteDialog />

      {/* Compare Floating Button */}
      {selectedPolicies.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'primary.main',
            color: 'white',
            px: 3,
            py: 2,
            borderRadius: 2,
            boxShadow: 3,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CompareArrows />
          <Typography>
            {selectedPolicies.length} policies selected
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => setCompareDialog(true)}
          >
            Compare Now
          </Button>
        </Box>
      )}
    </>
  );
};

export default InsuranceMarketplace;